// src/pages/builder/RewardsSettingsPanel.js
//
// REWARDS & AVATAR SETTINGS — one admin place for everything about avatars
// and everything that costs or pays coins (Layout Builder).
//
//   🛍 Shop items       hats, crowns, wings, pets, frames, pins, power-ups:
//                       add new ones (AI picture or upload), set prices,
//                       put on sale / hide            (shopItems collection)
//   🎨 Avatar maker     clothes, clothes colours, hair colours, glasses,
//                       sunglasses, pins: change any price, or hide an item
//   🪙 Coin amounts     what games, first plays, Treasure Quest, teacher
//                       gifts, the daily trail and sticker albums pay
//
// Avatar prices and coin amounts are saved to rewardSettings/main. The
// Cloud Functions read the same doc (functions/settings.js), so the price
// shown here is the price students pay. Empty box = the built-in default.

import React, { useEffect, useMemo, useState } from "react";
import { ShopItemsManager } from "./ShopItemsPanel";
import AvatarItemsManager from "./AvatarItemsManager";
import { useAvatarItemsVersion } from "../../services/avatarItemsService";
import { AvatarFace, DEFAULT_LOOK } from "../../components/avatar/avatarParts";
import { CATALOG, LEVEL_NAMES, basePriceOf } from "../../components/avatar/avatarCatalog";
import { DEFAULT_RULES, saveRewardSettings, useRewardSettings } from "../../services/rewardSettingsService";
import PromoSettings from "./PromoSettings";
import "./rewardsSettings.css";

const GROUPS = [
  ["top", "Clothes"],
  ["color", "Clothes colours"],
  ["haircolor", "Hair colours"],
  ["glasses", "Glasses"],
  ["sunglasses", "Sunglasses"],
  ["pin", "Pins"],
  ["eyes", "Special eyes"],
  ["mouth", "Special mouths"],
  ["mustache", "Mustaches & beards"],
  ["pants", "Pants"],
  ["shoes", "Shoes & sneakers"],
];
const LOOK_KEY = { top: "top", color: "topColor", haircolor: "hairColor", glasses: "glasses", sunglasses: "glasses", pin: "pin", eyes: "eyesItem", mouth: "mouthItem", mustache: "mustache", pants: "pants", shoes: "shoes" };
const FULL_KINDS = new Set(["pants", "shoes"]); // only show on the full body
const ZOOM = { glasses: "is-eyes", sunglasses: "is-eyes", pin: "is-pin", haircolor: "is-head", eyes: "is-eyes", mouth: "is-eyes", mustache: "is-head" };

const RULE_FIELDS = [
  {
    title: "Games",
    hint: "Paid once at the end of every game (Final Score screen).",
    fields: [
      [["game", "finish"], "Coins for finishing a game"],
      [["game", "coinsPerCorrect"], "Coins per correct answer"],
      [["game", "maxFromQuiz"], "Most coins from correct answers per game"],
      [["game", "maxFromBonus"], "Most extra coins from the bonus round"],
      [["game", "bonusPointsPerCoin"], "Bonus points per extra coin"],
      [["game", "replaySameDay"], "Same topic again the same day pays"],
      [["game", "dailyCap"], "Most coins from games per day"],
    ],
  },
  {
    title: "First time watching / listening",
    hint: "Once per topic, ever.",
    fields: [
      [["firstPlay", "video"], "Video"],
      [["firstPlay", "music"], "Song"],
      [["firstPlay", "singalong"], "Sing-along"],
    ],
  },
  {
    title: "Treasure Quest",
    fields: [
      [["quest", "treasure"], "Coins in the treasure chest"],
      [["quest", "treasuresPerDay"], "Chests that pay per day"],
    ],
  },
  {
    title: "Teacher gifts",
    fields: [
      [["gift", "maxPerGift"], "Most coins in one gift"],
      [["gift", "perStudentPerDay"], "Most gift coins per student per day"],
    ],
  },
  {
    title: "Daily treasure trail",
    hint: "Day 7 is always the golden chest.",
    fields: [0, 1, 2, 3, 4, 5].map((i) => [["trail", "coins", i], `Day ${i + 1}`]),
  },
  {
    title: "Sticker albums & chests",
    fields: [
      [["album", "coins"], "Coins for finishing an album"],
      [["duplicateCoins"], "Coins for a sticker you already have"],
    ],
  },
];

const getPath = (obj, path) => path.reduce((o, k) => (o == null ? undefined : o[k]), obj);
const setPath = (obj, path, value) => {
  const out = Array.isArray(obj) ? [...obj] : { ...(obj || {}) };
  const [k, ...rest] = path;
  if (!rest.length) {
    if (value === "" || value === undefined) {
      if (Array.isArray(out)) out[k] = null;
      else delete out[k];
    } else out[k] = value;
  } else out[k] = setPath(out[k] ?? (typeof rest[0] === "number" ? [] : {}), rest, value);
  return out;
};
const cleanNum = (v) => {
  if (v === "" || v === null || v === undefined) return "";
  const n = Math.max(0, Math.min(100000, Math.floor(Number(v))));
  return Number.isFinite(n) ? n : "";
};

const RewardsSettingsPanel = () => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("shop");
  const { settings, loaded } = useRewardSettings();
  const [rules, setRules] = useState(null);
  const [avatar, setAvatar] = useState(null);
  const [group, setGroup] = useState("top");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  // start editing from what is saved (once loaded)
  useEffect(() => {
    if (!loaded || rules !== null) return;
    setRules(settings.rules || {});
    setAvatar({ prices: { ...(settings.avatar?.prices || {}) }, hidden: { ...(settings.avatar?.hidden || {}) } });
  }, [loaded, settings, rules]);

  const dirty = useMemo(() => {
    if (!rules || !avatar) return false;
    return (
      JSON.stringify(rules) !== JSON.stringify(settings.rules || {}) ||
      JSON.stringify(avatar) !== JSON.stringify({ prices: settings.avatar?.prices || {}, hidden: settings.avatar?.hidden || {} })
    );
  }, [rules, avatar, settings]);

  const save = async () => {
    setBusy(true);
    setNotice(null);
    try {
      await saveRewardSettings({ rules, avatar });
      setNotice({ tone: "ok", text: "Saved. Students see the new prices and amounts right away." });
    } catch (err) {
      setNotice({ tone: "bad", text: err?.code === "permission-denied" ? "Permission denied — deploy the firestore.rules with the rewardSettings block, and sign in as an admin." : err?.message || String(err) });
    } finally {
      setBusy(false);
    }
  };

  const undo = () => {
    setRules(settings.rules || {});
    setAvatar({ prices: { ...(settings.avatar?.prices || {}) }, hidden: { ...(settings.avatar?.hidden || {}) } });
    setNotice(null);
  };

  useAvatarItemsVersion(); // uploaded items show here too, for prices / hiding
  const items = CATALOG.filter((i) => i.kind === group && !i.exclusive && !i.preview);
  const prizes = CATALOG.filter((i) => i.exclusive);

  const setPrice = (id, v) => {
    const n = cleanNum(v);
    setAvatar((a) => {
      const prices = { ...a.prices };
      if (n === "" || n === basePriceOf(id)) delete prices[id];
      else prices[id] = n;
      return { ...a, prices };
    });
  };
  const setHidden = (id, on) =>
    setAvatar((a) => {
      const hidden = { ...a.hidden };
      if (on) hidden[id] = true;
      else delete hidden[id];
      return { ...a, hidden };
    });
  const setGroupPrice = (list, v) => list.forEach((it) => setPrice(it.id, v));

  const renderRows = (list) => (
    <div className="rws-rows">
      {list.map((it) => {
        const base = basePriceOf(it.id);
        const over = avatar.prices[it.id];
        const hidden = Boolean(avatar.hidden[it.id]);
        return (
          <div key={it.id} className={`rws-row ${hidden ? "is-hidden" : ""}`}>
            <span className={`rws-face ${ZOOM[it.kind] || ""}`}>
              <AvatarFace look={{ ...DEFAULT_LOOK, [LOOK_KEY[it.kind]]: it.id }} full={FULL_KINDS.has(it.kind)} />
            </span>
            <span className="rws-name">
              <b>{it.name}</b>
              <small>
                {it.level ? `Level ${it.level} · ` : ""}default {base === 0 ? "free" : `${base} coins`}
              </small>
            </span>
            <label className="rws-price">
              <span>Price</span>
              <input type="number" min="0" step="1" value={over ?? ""} placeholder={String(base)} onChange={(e) => setPrice(it.id, e.target.value)} />
            </label>
            <label className="rws-hide">
              <input type="checkbox" checked={hidden} onChange={(e) => setHidden(it.id, e.target.checked)} /> Hidden
            </label>
          </div>
        );
      })}
    </div>
  );

  return (
    <section className="lb-card rws-panel">
      <div className="lb-card-head">
        <span className="lb-step rws-step">🪙</span>
        <div>
          <h2>Rewards &amp; avatar settings</h2>
          <p className="lb-hint">Everything about avatars and everything that costs or pays coins, in one place.</p>
        </div>
        <button type="button" className="lb-btn" onClick={() => setOpen((v) => !v)}>
          {open ? "Hide" : "Open"}
        </button>
      </div>

      {open && (
        <>
          <nav className="rws-tabs" role="tablist">
            {[
              ["shop", "🛍 Shop items", "hats, crowns, wings, pets, frames, pins, power-ups"],
              ["upload", "🧥 Upload avatar items", "your own jackets, sneakers, beards"],
              ["avatar", "🎨 Avatar maker", "clothes, jackets, shoes, beards, glasses, pins"],
              ["coins", "🪙 Coin amounts", "games, trail, gifts, albums"],
              ["promos", "📣 Educator promotions", "coin discount & product code popups"],
            ].map(([id, label, sub]) => (
              <button key={id} type="button" role="tab" aria-selected={tab === id} className={tab === id ? "is-on" : ""} onClick={() => setTab(id)}>
                {label}
                <small>{sub}</small>
              </button>
            ))}
          </nav>

          {tab === "shop" && (
            <div className="rws-body">
              <p className="lb-hint">
                Add new hats, crowns (pick “Hats”), wings, pets, frames and pins — with an AI picture or your own upload — and set
                what each one costs. Price 0 = free.
              </p>
              <ShopItemsManager />
            </div>
          )}

          {tab === "upload" && (
            <div className="rws-body">
              <AvatarItemsManager />
            </div>
          )}

          {tab !== "shop" && tab !== "upload" && (!rules || !avatar) && <p className="lb-hint">Loading settings…</p>}

          {tab === "avatar" && avatar && (
            <div className="rws-body">
              <p className="lb-hint">
                Leave a price box empty to use the default. “Hidden” takes an item out of the avatar maker for students who don't
                own it (anyone who already has it keeps it). Skin tones, hairstyles, faces and backgrounds are always free.
              </p>
              <span className="shp-seg rws-seg" role="group" aria-label="Item group">
                {GROUPS.map(([k, label]) => (
                  <button key={k} type="button" className={group === k ? "is-on" : ""} onClick={() => setGroup(k)}>
                    {label}
                  </button>
                ))}
                <button type="button" className={group === "prizes" ? "is-on" : ""} onClick={() => setGroup("prizes")}>
                  Album prizes
                </button>
              </span>

              {group === "prizes" ? (
                <div className="rws-prizes">
                  <p className="lb-hint">These colours can't be bought — students win one by finishing a sticker album.</p>
                  {prizes.length > 0 && (
                    <div className="rws-rows">
                      {prizes.map((it) => (
                        <div key={it.id} className="rws-row">
                          <span className="rws-face">
                            <AvatarFace look={{ ...DEFAULT_LOOK, topColor: it.id }} />
                          </span>
                          <span className="rws-name">
                            <b>{it.name}</b>
                            <small>album prize</small>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : LEVEL_NAMES[group] ? (
                // glasses, sunglasses, and the picture jackets / shoes / beards:
                // the drawn items first, then each level with its own price box
                [0, 1, 2, 3, 4].map((lv) => {
                  if (lv === 0) {
                    const drawn = items.filter((i) => !i.level);
                    return drawn.length ? <div key="drawn">{renderRows(drawn)}</div> : null;
                  }
                  const list = items.filter((i) => i.level === lv);
                  if (!list.length) return null;
                  return (
                    <div key={lv} className="rws-level">
                      <div className="rws-level-head">
                        <h4>
                          Level {lv} — {LEVEL_NAMES[group][lv]}
                        </h4>
                        <label>
                          Set the whole level to{" "}
                          <input type="number" min="0" placeholder="coins" onKeyDown={(e) => e.key === "Enter" && setGroupPrice(list, e.currentTarget.value)} onBlur={(e) => e.currentTarget.value !== "" && setGroupPrice(list, e.currentTarget.value)} />
                        </label>
                      </div>
                      {renderRows(list)}
                    </div>
                  );
                })
              ) : (
                renderRows(items)
              )}
            </div>
          )}

          {tab === "coins" && rules && (
            <div className="rws-body">
              <p className="lb-hint">Leave a box empty to use the default (shown in grey).</p>
              <div className="rws-rule-grid">
                {RULE_FIELDS.map((g) => (
                  <fieldset key={g.title} className="rws-rule-group">
                    <legend>{g.title}</legend>
                    {g.hint && <p className="rws-small">{g.hint}</p>}
                    {g.fields.map(([path, label]) => (
                      <label key={path.join(".")} className="rws-rule">
                        <span>{label}</span>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={getPath(rules, path) ?? ""}
                          placeholder={String(getPath(DEFAULT_RULES, path))}
                          onChange={(e) => setRules((r) => setPath(r, path, cleanNum(e.target.value)))}
                        />
                      </label>
                    ))}
                  </fieldset>
                ))}
              </div>
              <p className="rws-small">Chest odds (coins, stickers, power-ups) live in rewardsData.json.</p>
            </div>
          )}

          {tab === "promos" && (
            <div className="rws-body">
              <PromoSettings />
            </div>
          )}

          {tab !== "shop" && tab !== "promos" && rules && (
            <div className="rws-save">
              {notice && <span className={`shp-msg is-${notice.tone}`}>{notice.text}</span>}
              <button type="button" className="lb-btn lb-btn-soft" onClick={undo} disabled={!dirty || busy}>
                Undo changes
              </button>
              <button type="button" className="lb-btn" onClick={save} disabled={!dirty || busy}>
                {busy ? "Saving…" : dirty ? "Save settings" : "Saved ✓"}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default RewardsSettingsPanel;
